/**
 * Labels PT-BR + helpers dos participantes de um Bond (papel + permissões = ACL,
 * ADR-130). 3º sub-recurso ortogonal do modelo Bond (ver [[feedback_modelo_bond_3_entidades]]).
 *
 * permissions / permissionScope / side vêm de enums canônicos `@lume/shared`
 * (importados, não redefinidos) — só os rótulos são locais, com verificação de
 * exaustividade. `role` é texto livre no DTO (`@IsString`, validado por bond.type
 * no Registry), então oferecemos sugestões (datalist) sem restringir.
 */

import {
  BOND_PERMISSION_KEYS,
  BOND_SIDES,
  PERMISSION_SCOPES,
  type BondPermissionKey,
  type BondSide,
  type PermissionScope,
} from '@lume/shared';

export type ParticipantStatus = 'active' | 'revoked';

export const PERMISSION_LABELS: Record<BondPermissionKey, string> = {
  read: 'Ler',
  'read-redacted': 'Ler (redigido)',
  write: 'Escrever',
  share: 'Compartilhar',
  delete: 'Excluir',
  admin: 'Administrar',
  export: 'Exportar',
  'audit-view': 'Ver auditoria',
};

export const SCOPE_LABELS: Record<PermissionScope, string> = {
  full: 'Completo',
  'messages-only': 'Só mensagens',
  'metadata-only': 'Só metadados',
  'specific-fields': 'Campos específicos',
};

export const SIDE_LABELS: Record<BondSide, string> = {
  seller: 'Vendedor',
  buyer: 'Comprador',
  neutral: 'Neutro',
  mediator: 'Mediador',
};

// Sugestões de papel (datalist) — catálogo de contrato (CONTRACT_BOND_ROLES).
// Não restringe: o DTO aceita qualquer string (validação por bond.type no Registry).
export const ROLE_SUGGESTIONS: readonly string[] = [
  'holder',
  'authorized-contact',
  'account-manager',
  'support-agent',
];

const ROLE_LABELS: Record<string, string> = {
  holder: 'Titular',
  'authorized-contact': 'Contato autorizado',
  'account-manager': 'Gerente de conta',
  'support-agent': 'Atendente de suporte',
};

// Exaustividade: todo enum tem rótulo (falha em build se o enum crescer sem label).
const PERMISSION_MISSING = BOND_PERMISSION_KEYS.filter((k) => !(k in PERMISSION_LABELS));
const SCOPE_MISSING = PERMISSION_SCOPES.filter((s) => !(s in SCOPE_LABELS));
const SIDE_MISSING = BOND_SIDES.filter((s) => !(s in SIDE_LABELS));
if (PERMISSION_MISSING.length > 0 || SCOPE_MISSING.length > 0 || SIDE_MISSING.length > 0) {
  throw new Error(
    `Labels de participante desatualizados — permissions: ${PERMISSION_MISSING.join(',')} | ` +
      `scopes: ${SCOPE_MISSING.join(',')} | sides: ${SIDE_MISSING.join(',')}`,
  );
}

/** Rótulo de permissão; fallback para o código cru (forward-compat). */
export function permissionLabel(key: string): string {
  return PERMISSION_LABELS[key as BondPermissionKey] ?? key;
}

export function scopeLabel(scope: string): string {
  return SCOPE_LABELS[scope as PermissionScope] ?? scope;
}

export function sideLabel(side: string | null): string {
  if (side === null) return 'Sem lado';
  return SIDE_LABELS[side as BondSide] ?? side;
}

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export function statusLabel(status: ParticipantStatus): string {
  return status === 'active' ? 'Ativo' : 'Revogado';
}

/** Data ISO → dd/mm/aaaa; null/inválida → '—'. */
export function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', { dateStyle: 'medium' });
}
